import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin } from 'lucide-react';
import chinaGeoJSON from '@/assets/map-data/100000_full.json';
import shanghaiGeoJSON from '@/assets/map-data/310000_full.json';

export interface Warehouse {
  id: string;
  name: string;
  type: 'retail' | 'factory';
  city: string;
  coordinates: [number, number]; // [经度, 纬度]
  status: 'normal' | 'warning' | 'alert';
  inventory?: number;
}

export interface TransferOrder {
  id: string;
  from: string;
  to: string;
  status: 'pending' | 'in_transit' | 'completed';
  isCompleted: boolean;
  vehicle: string;
  createTime?: string;
  estimatedArrival?: string;
  quantity?: number;
}

export interface CrossMapLine {
  from: {
    map: 'china' | 'shanghai';
    coordinates: [number, number];
  };
  to: {
    map: 'china' | 'shanghai';
    coordinates: [number, number];
  };
  color?: string;
  dashed?: boolean;
}

interface EChartsMapProps {
  warehouses?: Warehouse[];
  transferOrders?: TransferOrder[];
  crossMapLines?: CrossMapLine[];
  onWarehouseClick?: (warehouse: Warehouse) => void;
  onVehicleClick?: (order: TransferOrder) => void;
  className?: string;
}

// 仓库坐标配置
const warehouseLocations = {
  zhengzhou: {
    id: 'zz',
    name: '郑州仓',
    type: 'factory' as const,
    city: '郑州',
    coordinates: [113.665412, 34.757975] as [number, number],
    status: 'normal' as const
  },
  guangzhou: {
    id: 'gz',
    name: '广州仓',
    type: 'factory' as const,
    city: '广州',
    coordinates: [113.280637, 23.125178] as [number, number],
    status: 'normal' as const
  },
  xixi: {
    id: 'xx',
    name: '西西仓',
    type: 'retail' as const,
    city: '上海宝山',
    coordinates: [121.4804, 31.4051] as [number, number],
    status: 'normal' as const
  },
  chengpin: {
    id: 'cp',
    name: '成品仓',
    type: 'retail' as const,
    city: '上海宝山',
    coordinates: [121.4904, 31.4151] as [number, number],
    status: 'normal' as const
  }
};

// 默认的仓库列表
const defaultWarehouses: Warehouse[] = [
  warehouseLocations.zhengzhou,
  warehouseLocations.guangzhou,
  warehouseLocations.xixi,
  warehouseLocations.chengpin
];

// 彩色分区配色
const chinaColors = [
  '#fbbf24', '#60a5fa', '#34d399', '#f472b6', '#a78bfa', '#f87171', '#facc15', '#38bdf8', '#4ade80', '#f472b6', '#818cf8', '#fb7185', '#fcd34d', '#5eead4', '#c084fc', '#fca5a5', '#fde68a', '#a3e635', '#f9a8d4', '#7dd3fc', '#bef264', '#fda4af', '#fef08a', '#bbf7d0', '#f3e8ff', '#fef9c3', '#d1fae5', '#f1f5f9', '#e0e7ff', '#f3f4f6', '#f5d0fe', '#f0fdf4', '#fef2f2', '#f1f5f9'
];
const shColors = [
  '#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#f87171', '#facc15', '#38bdf8', '#4ade80', '#f472b6', '#818cf8', '#fb7185', '#fcd34d', '#5eead4', '#c084fc', '#fca5a5'
];

// 获取所有省份/直辖市名
function getChinaRegions(geojson: any) {
  return geojson.features.map((f: any, i: number) => ({
    name: f.properties.name,
    itemStyle: { areaColor: chinaColors[i % chinaColors.length] }
  }));
}
// 获取所有上海区县名
function getShanghaiRegions(geojson: any) {
  return geojson.features.map((f: any, i: number) => ({
    name: f.properties.name,
    itemStyle: { areaColor: shColors[i % shColors.length] }
  }));
}

// 添加山西省的坐标
const shanxiWarehouse: Warehouse = {
  id: 'sx',
  name: '山西仓',
  type: 'factory',
  city: '山西',
  coordinates: [112.549248, 37.857014] as [number, number], // 显式类型标注
  status: 'normal',
};

// 添加黑龙江省的坐标
const heilongjiangWarehouse: Warehouse = {
  id: 'hlj',
  name: '黑龙江仓',
  type: 'factory',
  city: '黑龙江',
  coordinates: [126.642464, 45.756967] as [number, number], // 黑龙江省中心坐标
  status: 'normal',
};

// 更新宝山区的坐标（靠近崇明区的位置）
const baoshanWarehouse: Warehouse = {
  id: 'bs',
  name: '宝山仓',
  type: 'retail',
  city: '上海宝山',
  coordinates: [121.4804, 31.5051] as [number, number], // 更新为更准确的宝山区坐标
  status: 'normal',
};

// 修改上海地图配置
const shOption = {
  backgroundColor: '#f8fafc',
  title: {
    text: '上海地图',
    left: 'center',
    textStyle: { color: '#1e293b', fontSize: 16, fontWeight: 'bold' }
  },
  geo: {
    map: 'shanghai',
    roam: true,
    zoom: 1.2,
    center: [121.48941, 31.40527],
    itemStyle: { 
      areaColor: '#e2e8f0', 
      borderColor: '#cbd5e1', 
      borderWidth: 1 
    },
    emphasis: { 
      itemStyle: { 
        areaColor: '#f1f5f9' 
      } 
    },
    label: {
      show: true,
      color: '#334155',
      fontSize: 12,
      fontWeight: 'normal',
      position: 'center'
    },
    regions: getShanghaiRegions(shanghaiGeoJSON)
  },
  series: [
    {
      name: '仓库',
      type: 'scatter',
      coordinateSystem: 'geo',
      data: [
        { 
          name: warehouseLocations.xixi.name, 
          value: warehouseLocations.xixi.coordinates,
          itemStyle: {
            color: '#3b82f6'
          }
        },
        { 
          name: warehouseLocations.chengpin.name, 
          value: warehouseLocations.chengpin.coordinates,
          itemStyle: {
            color: '#3b82f6'
          }
        }
      ],
      symbol: 'circle',
      symbolSize: 16,
      label: { show: false },
      emphasis: {
        scale: 1.2,
        label: {
          show: true,
          position: 'top',
          formatter: '{b}'
        }
      }
    }
  ]
};

// 修改默认连线配置
const defaultCrossMapLines: CrossMapLine[] = [
  {
    from: {
      map: 'shanghai',
      coordinates: warehouseLocations.xixi.coordinates
    },
    to: {
      map: 'china',
      coordinates: warehouseLocations.zhengzhou.coordinates
    },
    color: '#3b82f6',
    dashed: true
  }
];

export default function EChartsMap({
  warehouses = defaultWarehouses, // 使用默认仓库列表
  transferOrders = [],
  crossMapLines = defaultCrossMapLines,
  onWarehouseClick,
  onVehicleClick,
  className = ""
}: EChartsMapProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const shanghaiChartRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const shanghaiChartInstance = useRef<echarts.ECharts | null>(null);
  const [chinaPixel, setChinaPixel] = useState<[number, number] | null>(null);
  const [shanghaiPixel, setShanghaiPixel] = useState<[number, number] | null>(null);
  const [shanghaiOffset, setShanghaiOffset] = useState<{left: number, top: number}>({left: 0, top: 0});
  const [containerHeight, setContainerHeight] = useState(600);
  const [svgOffsetTop, setSvgOffsetTop] = useState(0);
  const [shanghaiSvgOffsetTop, setShanghaiSvgOffsetTop] = useState(0);
  // 跨地图连线像素坐标
  const [crossLinePixels, setCrossLinePixels] = useState<{
    from: [number, number],
    to: [number, number],
    color?: string,
    dashed?: boolean
  }[]>([]);

  useEffect(() => {
    try {
      echarts.registerMap('china', chinaGeoJSON as any);
      echarts.registerMap('shanghai', shanghaiGeoJSON as any);
      setMapLoaded(true);
    } catch (error) {
      setLoadError('地图数据加载失败');
      setMapLoaded(true);
    }
  }, []);

  // 动态获取中国地图容器高度和offsetTop
  useEffect(() => {
    if (chartRef.current) {
      setContainerHeight(chartRef.current.offsetHeight);
      setSvgOffsetTop(chartRef.current.offsetTop);
    }
    if (shanghaiChartRef.current) {
      setShanghaiSvgOffsetTop(shanghaiChartRef.current.offsetTop);
    }
  }, [mapLoaded]);

  // 添加滚动处理
  useEffect(() => {
    const handleScroll = () => {
      if (chartRef.current && shanghaiChartRef.current) {
        const shRect = shanghaiChartRef.current.getBoundingClientRect();
        const cnRect = chartRef.current.getBoundingClientRect();
        setShanghaiOffset({
          left: shRect.left - cnRect.left,
          top: shRect.top - cnRect.top
        });
        setSvgOffsetTop(chartRef.current.offsetTop - window.scrollY);
        setShanghaiSvgOffsetTop(shanghaiChartRef.current.offsetTop - window.scrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 中国地图渲染
  useEffect(() => {
    if (!chartRef.current || !mapLoaded) return;
    chartInstance.current = echarts.init(chartRef.current);

    // 生成散点数据
    const retailWarehouses = warehouses.filter((w: Warehouse) => w.type === 'retail').map((w: Warehouse) => ({
      name: w.name,
      value: [...w.coordinates, w.inventory || 100],
      itemStyle: {
        color: w.status === 'alert' ? '#ef4444' : w.status === 'warning' ? '#f59e0b' : '#3b82f6'
      },
      symbolSize: 20,
      data: w
    }));
    const factoryWarehouses = warehouses.filter((w: Warehouse) => w.type === 'factory').map((w: Warehouse) => ({
      name: w.name,
      value: [...w.coordinates, w.inventory || 100],
      itemStyle: {
        color: w.status === 'alert' ? '#ef4444' : w.status === 'warning' ? '#f59e0b' : '#10b981'
      },
      symbolSize: 18,
      data: w
    }));
    // 迁移线数据（如有需要可恢复）
    // const lines = transferOrders.map((order: TransferOrder) => { ... });

    // 修改中国地图配置
    const option = {
      backgroundColor: '#f8fafc',
      title: {
        text: '物流调拨监控',
        left: 'center',
        top: 10,
        textStyle: {
          color: '#1e293b',
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          if (params.seriesType === 'scatter') {
            const warehouse = params.data.data;
            return `
              <div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${warehouse.name}</div>
                <div>类型: ${warehouse.type === 'retail' ? '零售仓' : '工厂仓'}</div>
                <div>城市: ${warehouse.city}</div>
                <div>状态: ${warehouse.status === 'alert' ? '告警' : warehouse.status === 'warning' ? '预警' : '正常'}</div>
              </div>
            `;
          }
          return params.name;
        }
      },
      legend: {
        data: ['零售仓', '工厂仓'],
        left: 'left',
        bottom: 10,
        textStyle: {
          color: '#64748b'
        }
      },
      geo: {
        map: 'china',
        roam: true,
        zoom: 1.3, // 增大缩放比例
        center: [104, 36], // 调整中心点位置
        aspectScale: 0.85, // 调整地图长宽比
        layoutSize: '95%', // 利用更多空间
        itemStyle: {
          areaColor: '#e2e8f0',
          borderColor: '#94a3b8',
          borderWidth: 1.5,
          shadowColor: 'rgba(0, 0, 0, 0.1)',
          shadowBlur: 5
        },
        emphasis: {
          itemStyle: {
            areaColor: '#f1f5f9',
            borderWidth: 2,
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          },
          label: {
            color: '#1e293b',
            fontSize: 14,
            fontWeight: 'bold'
          }
        },
        label: {
          show: false // 默认不显示省份名称
        },
        regions: getChinaRegions(chinaGeoJSON),
        silent: false, // 允许地图交互
        scaleLimit: { // 限制缩放范围
          min: 1,
          max: 3
        }
      },
      series: [
        {
          name: '零售仓',
          type: 'scatter',
          coordinateSystem: 'geo',
          data: retailWarehouses,
          symbol: 'circle',
          symbolSize: 16,
          label: { show: false },
          emphasis: {
            scale: 1.2,
            label: {
              show: true,
              position: 'top',
              formatter: '{b}'
            }
          }
        },
        {
          name: '工厂仓',
          type: 'scatter',
          coordinateSystem: 'geo',
          data: factoryWarehouses,
          symbol: 'rect',
          symbolSize: 16,
          label: { show: false },
          emphasis: {
            scale: 1.2,
            label: {
              show: true,
              position: 'top',
              formatter: '{b}'
            }
          }
        }
      ]
    };
    chartInstance.current.setOption(option);

    // 计算郑州仓在中国地图上的像素坐标
    const updateChinaPixel = () => {
      if (!chartInstance.current) return;
      const pixel = chartInstance.current.convertToPixel('geo', warehouseLocations.zhengzhou.coordinates);
      if (Array.isArray(pixel)) {
        setChinaPixel(pixel as [number, number]);
      }
    };
    updateChinaPixel();
    chartInstance.current.on('georoam', updateChinaPixel);
    window.addEventListener('resize', updateChinaPixel);

    // 注册点击事件
    chartInstance.current.on('click', (params: any) => {
      if (params.seriesType === 'scatter' && params.data.data) {
        onWarehouseClick?.(params.data.data);
      }
    });

    return () => {
      chartInstance.current?.off('georoam', updateChinaPixel);
      window.removeEventListener('resize', updateChinaPixel);
      chartInstance.current?.dispose();
    };
  }, [mapLoaded, warehouses, onWarehouseClick]);

  // 上海地图渲染
  useEffect(() => {
    if (!shanghaiChartRef.current || !mapLoaded) return;
    shanghaiChartInstance.current = echarts.init(shanghaiChartRef.current);
    shanghaiChartInstance.current.setOption(shOption);

    // 注册点击事件
    shanghaiChartInstance.current.on('click', (params: any) => {
      if (params.seriesType === 'scatter' && params.data.data) {
        onWarehouseClick?.(params.data.data);
      } else if (params.seriesType === 'lines' && params.data.data) {
        onVehicleClick?.(params.data.data);
      }
    });

    // 计算宝山仓在上海地图上的像素坐标和容器偏移
    const updateShanghaiPixel = () => {
      if (!shanghaiChartInstance.current || !shanghaiChartRef.current || !chartRef.current) return;
      const pixel = shanghaiChartInstance.current.convertToPixel('geo', warehouseLocations.xixi.coordinates);
      if (Array.isArray(pixel)) {
        setShanghaiPixel(pixel as [number, number]);
      }

      const shRect = shanghaiChartRef.current.getBoundingClientRect();
      const cnRect = chartRef.current.getBoundingClientRect();
      setShanghaiOffset({
        left: shRect.left - cnRect.left,
        top: shRect.top - cnRect.top
      });
    };
    updateShanghaiPixel();
    shanghaiChartInstance.current.on('georoam', updateShanghaiPixel);
    window.addEventListener('resize', updateShanghaiPixel);

    return () => {
      shanghaiChartInstance.current?.off('georoam', updateShanghaiPixel);
      window.removeEventListener('resize', updateShanghaiPixel);
      shanghaiChartInstance.current?.dispose();
    };
  }, [mapLoaded]);

  // 计算所有跨地图连线的像素坐标
  useEffect(() => {
    if (!mapLoaded) return;
    const result: {
      from: [number, number],
      to: [number, number],
      color?: string,
      dashed?: boolean
    }[] = [];
    crossMapLines.forEach(line => {
      let fromPixel: [number, number] | null = null;
      let toPixel: [number, number] | null = null;
      if (line.from.map === 'china' && chartInstance.current) {
        fromPixel = chartInstance.current.convertToPixel('geo', line.from.coordinates) as [number, number];
      } else if (line.from.map === 'shanghai' && shanghaiChartInstance.current) {
        fromPixel = shanghaiChartInstance.current.convertToPixel('geo', line.from.coordinates) as [number, number];
        // 需要加上shanghaiOffset
        fromPixel = [fromPixel[0] + shanghaiOffset.left, fromPixel[1] + shanghaiOffset.top - svgOffsetTop - shanghaiSvgOffsetTop];
      }
      if (line.to.map === 'china' && chartInstance.current) {
        toPixel = chartInstance.current.convertToPixel('geo', line.to.coordinates) as [number, number];
      } else if (line.to.map === 'shanghai' && shanghaiChartInstance.current) {
        toPixel = shanghaiChartInstance.current.convertToPixel('geo', line.to.coordinates) as [number, number];
        // 需要加上shanghaiOffset
        toPixel = [toPixel[0] + shanghaiOffset.left, toPixel[1] + shanghaiOffset.top - svgOffsetTop - shanghaiSvgOffsetTop];
      }
      if (fromPixel && toPixel) {
        result.push({
          from: fromPixel,
          to: toPixel,
          color: line.color,
          dashed: line.dashed
        });
      }
    });
    setCrossLinePixels(result);
  }, [crossMapLines, mapLoaded, shanghaiOffset, svgOffsetTop, shanghaiSvgOffsetTop]);

  // 叠加SVG连线
  const svgLine = (chinaPixel && shanghaiPixel)
    ? (
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 600,
          pointerEvents: 'none',
          zIndex: 3
        }}
      >
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="8" refY="4" orient="auto">
            <polygon points="0 0, 8 4, 0 8" fill="#f59e0b" />
          </marker>
        </defs>
        <line
          x1={chinaPixel[0]}
          y1={chinaPixel[1]}
          x2={shanghaiPixel[0] + shanghaiOffset.left}
          y2={shanghaiPixel[1] + shanghaiOffset.top}
          stroke="#f59e0b"
          strokeWidth={3}
          strokeDasharray="8 4"
          markerEnd="url(#arrowhead)"
        />
      </svg>
    ) : null;

  // 修改 SVG 连线样式
  return (
    <div className="relative w-full h-[800px] p-4">
      <div className="grid grid-cols-4 gap-6 h-full">
        {/* 中国地图 - 占据左侧3/4 */}
        <div className="col-span-3 relative">
          <Card className={`w-full h-full ${className} overflow-hidden`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                智能物流地图
              </CardTitle>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="text-blue-600 border-blue-200">
                  零售仓: {warehouses.filter(w => w.type === 'retail').length}个
                </Badge>
                <Badge variant="outline" className="text-green-600 border-green-200">
                  工厂仓: {warehouses.filter(w => w.type === 'factory').length}个
                </Badge>
                <Badge variant="outline" className="text-yellow-600 border-yellow-200">
                  调拨单: {transferOrders.length}个
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 relative h-[calc(100%-80px)]"> {/* 调整内容区域高度 */}
              {!mapLoaded ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <Skeleton className="h-8 w-48 mx-auto" />
                    <Skeleton className="h-4 w-32 mx-auto" />
                    <div className="text-sm text-slate-600">正在加载地图数据...</div>
                  </div>
                </div>
              ) : (
                <div
                  ref={chartRef}
                  className="w-full h-full rounded-lg"
                />
              )}
              {loadError && (
                <div className="absolute top-2 right-2 bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                  {loadError}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* 上海地图 - 占据右侧1/3 */}
        <div className="col-span-1">
          <Card className={`w-full ${className}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="w-4 h-4" />
                上海区域地图
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div
                ref={shanghaiChartRef}
                style={{ width: '100%', height: 600 }}
                className="rounded-lg"
              />
            </CardContent>
          </Card>
        </div>

        {/* SVG连线层 */}
        {(chinaPixel && shanghaiPixel || crossLinePixels.length > 0) && (
          <svg
            className="fixed pointer-events-none"
            style={{
              zIndex: 10,
              top: svgOffsetTop,
              left: 0,
              width: '100%',
              height: '100vh'
            }}
          >
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                <path d="M0,0 L10,5 L0,10" fill="#3b82f6" />
              </marker>
              {/* 添加发光效果 */}
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {crossLinePixels.map((line, idx) => (
              <g key={idx} filter="url(#glow)">
                <line
                  x1={line.from[0]}
                  y1={line.from[1]}
                  x2={line.to[0]}
                  y2={line.to[1]}
                  stroke={line.color || '#3b82f6'}
                  strokeWidth={2}
                  strokeDasharray={line.dashed ? '8 4' : undefined}
                  markerEnd="url(#arrowhead)"
                />
                {/* 添加动画效果 */}
                <circle
                  cx={line.from[0]}
                  cy={line.from[1]}
                  r="4"
                  fill={line.color || '#3b82f6'}
                  opacity="0.6"
                >
                  <animate
                    attributeName="r"
                    values="4;8;4"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.6;0.2;0.6"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            ))}
          </svg>
        )}
      </div>
    </div>
  );
}

